import DatePicker from '@hero/ui-kit/components/DatePicker';
import Modal from '@hero/ui-kit/components/Modal';
import Button from '@hero/ui-kit/inputs/Button';
import XButton from '@hero/ui-kit/inputs/XButton';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import { format, isValid, subDays, subMonths } from 'date-fns';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import Filter from '../../components/Filter';
import { createUTCDateRangeForBackend, parseToDate } from '../../utils/date';
import { FilterChip, usePublishFilterChips } from '../FilterPanel/FilterChipsContext';
import FilterPillGroup from '../FilterPanel/FilterPillGroup';

interface DateFilterProps {
    searchParam: string;
    defaultLabel: string;
    disableFilter?: string[];
    // Render as a collapsible accordion section inside a shared popover (header
    // search filters) instead of a standalone dropdown.
    inline?: boolean;
}

const labelDateFormat = 'dd/MM/yyyy';

const formatRangeDate = (dates: string) => {
    const splitDates = dates.split('|');

    return `${format(parseToDate(splitDates[0]), labelDateFormat)} - ${format(
        parseToDate(splitDates[1]),
        labelDateFormat
    )}`;
};

const DateFilter: React.FC<DateFilterProps> = ({
    searchParam,
    defaultLabel,
    disableFilter = [],
    inline = false
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [showRangeModal, setShowRangeModal] = React.useState(false);
    const [startDate, setStartDate] = React.useState<Date | null>();
    const [endDate, setEndDate] = React.useState<Date | null>();

    const setQueryDateParam = React.useCallback(
        (date: string) => {
            setSearchParams((prev) => {
                prev.set(searchParam, date.replace(/T/g, ' '));

                prev.delete('offset');

                return prev;
            });
        },
        [searchParam, setSearchParams]
    );

    const updateStatusFilter = React.useCallback(
        (date: string) => {
            if (date === 'date-range') {
                setShowRangeModal(true);
            } else if (date === 'any-time') {
                setStartDate(null);
                setEndDate(null);
                setSearchParams((searchParams) => {
                    searchParams.delete(searchParam);

                    return searchParams;
                });
            } else {
                setStartDate(null);
                setEndDate(null);
                setQueryDateParam(date);
            }
        },
        [setSearchParams, searchParam, setQueryDateParam]
    );

    const today = new Date();

    // Use createUTCDateRangeForBackend for all predefined ranges
    const todayRange = createUTCDateRangeForBackend(
        format(today, 'yyyy-MM-dd'),
        format(today, 'yyyy-MM-dd')
    );
    const todayValue = `${todayRange.from}|${todayRange.to}`;

    const yesterdayRange = createUTCDateRangeForBackend(
        format(subDays(today, 1), 'yyyy-MM-dd'),
        format(subDays(today, 1), 'yyyy-MM-dd')
    );
    const yesterdayValue = `${yesterdayRange.from}|${yesterdayRange.to}`;

    const last7daysRange = createUTCDateRangeForBackend(
        format(subDays(today, 7), 'yyyy-MM-dd'),
        format(today, 'yyyy-MM-dd')
    );
    const last7daysValue = `${last7daysRange.from}|${last7daysRange.to}`;

    const last30daysRange = createUTCDateRangeForBackend(
        format(subDays(today, 30), 'yyyy-MM-dd'),
        format(today, 'yyyy-MM-dd')
    );
    const last30daysValue = `${last30daysRange.from}|${last30daysRange.to}`;

    const last3monthsRange = createUTCDateRangeForBackend(
        format(subMonths(today, 3), 'yyyy-MM-dd'),
        format(today, 'yyyy-MM-dd')
    );
    const last3monthsValue = `${last3monthsRange.from}|${last3monthsRange.to}`;

    const last12monthsRange = createUTCDateRangeForBackend(
        format(subMonths(today, 12), 'yyyy-MM-dd'),
        format(today, 'yyyy-MM-dd')
    );
    const last12monthsValue = `${last12monthsRange.from}|${last12monthsRange.to}`;

    const selectedFilter = searchParams.get(searchParam);

    const isRangeSelected =
        !!selectedFilter &&
        ![
            todayValue,
            yesterdayValue,
            last7daysValue,
            last30daysValue,
            last3monthsValue,
            last12monthsValue
        ].includes(selectedFilter || '');

    const dates = [
        {
            label: 'Any time',
            value: `any-time`,
            isSelected: false,
            isDisabled: false
        },
        {
            label: 'Today',
            value: todayValue,
            isSelected: todayValue === selectedFilter,
            isDisabled: disableFilter.includes('today')
        },
        {
            label: 'Yesterday',
            value: yesterdayValue,
            isSelected: yesterdayValue === selectedFilter,
            isDisabled: disableFilter.includes('yesterday')
        },
        {
            label: 'Last 7 days',
            value: last7daysValue,
            isSelected: last7daysValue === selectedFilter,
            isDisabled: disableFilter.includes('last_7_days')
        },
        {
            label: 'Last 30 days',
            value: last30daysValue,
            isSelected: last30daysValue === selectedFilter,
            isDisabled: disableFilter.includes('last_30_days')
        },
        {
            label: 'Last 3 months',
            value: last3monthsValue,
            isSelected: last3monthsValue === selectedFilter,
            isDisabled: disableFilter.includes('last_3_months')
        },
        {
            label: 'Last 12 months',
            value: last12monthsValue,
            isSelected: last12monthsValue === selectedFilter,
            isDisabled: disableFilter.includes('last_12_months')
        },
        ...(isRangeSelected
            ? [
                  {
                      label: formatRangeDate(selectedFilter),
                      value: 'date-range',
                      isSelected: true,
                      isDisabled: false
                  }
              ]
            : []),
        {
            label: 'Range...',
            value: 'date-range',
            isSelected: false,
            isDisabled: disableFilter.includes('range')
        }
    ];

    const filterLabel = React.useMemo(() => {
        const date = searchParams.get(searchParam);

        if (date) {
            const dates = date.split('|');

            if (todayValue === selectedFilter) {
                return `${defaultLabel}: ${format(new Date(), labelDateFormat)}`;
            }

            if (yesterdayValue === selectedFilter) {
                return `${defaultLabel}: ${format(subDays(today, 1), labelDateFormat)}`;
            }

            // For date ranges, parse UTC timestamps correctly by adding ' UTC' suffix
            return `${defaultLabel}: ${format(parseToDate(dates[0]), labelDateFormat)} - ${format(
                parseToDate(dates[1]),
                labelDateFormat
            )}`;
        }

        return defaultLabel;
    }, [searchParam, searchParams, defaultLabel, todayValue, selectedFilter, yesterdayValue]);

    const onChange = (dates: [start: Date, end: Date]) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.code === 'Enter' && event.target instanceof HTMLInputElement) {
            const value = event.target.value;
            const splitDates = value.split('-');

            const startDate =
                splitDates[0] && isValid(new Date(splitDates[0])) ? new Date(splitDates[0]) : null;

            const endDate =
                splitDates[1] && isValid(new Date(splitDates[1])) ? new Date(splitDates[1]) : null;

            setStartDate(startDate);
            setEndDate(endDate);
        }
    };

    const saveDateRange = () => {
        if (startDate && endDate) {
            const preparedStartDate = format(startDate, 'yyyy-MM-dd');
            const preparedEndDate = format(endDate, 'yyyy-MM-dd');

            const filter = createUTCDateRangeForBackend(preparedStartDate, preparedEndDate);

            setQueryDateParam(
                `${filter.from}|${filter.to}` // Use the formatted date range
            );
        }
        setShowRangeModal(false);
    };

    const date = searchParams.get(searchParam);

    // One chip for the active range; its label already carries the field name
    // (e.g. "Created: 01/01/2025 - …"). Removing it clears the date param.
    const dateChips = React.useMemo<FilterChip[]>(
        () =>
            date
                ? [
                      {
                          key: `${searchParam}:range`,
                          label: filterLabel,
                          onRemove: () => updateStatusFilter('any-time')
                      }
                  ]
                : [],
        [date, searchParam, filterLabel, updateStatusFilter]
    );
    usePublishFilterChips(searchParam, dateChips);

    return (
        <>
            <Filter
                hasFilters={typeof date === 'string'}
                filterLabel={filterLabel}
                ignoreClickOutside={showRangeModal}
                width={isRangeSelected ? '28rem' : '24rem'}
                inline={inline}
                heading={defaultLabel}
                count={date ? 1 : 0}
            >
                <FilterPillGroup
                    options={dates
                        .filter((date) => !date.isDisabled)
                        .map((date) => ({
                            value: date.value,
                            label: date.label,
                            active: date.isSelected,
                            key: date.label
                        }))}
                    onToggle={updateStatusFilter}
                />
            </Filter>
            <Modal externalControls={[showRangeModal, setShowRangeModal]} noDefaultPadding>
                <Section>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '2.4rem'
                        }}
                    >
                        <P size="large" strong noDefaultMargin>
                            Range...
                        </P>
                        <XButton alt="close" onClick={() => setShowRangeModal(false)} />
                    </div>
                    <DatePicker
                        //@ts-ignore
                        onChange={onChange}
                        onKeyDown={handleKeyDown}
                        startDate={startDate}
                        endDate={endDate}
                        //@ts-ignore
                        selectsRange={true}
                        inline
                    />
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: '1rem'
                        }}
                    >
                        <Button
                            size="small"
                            variant="secondary"
                            onClick={() => setShowRangeModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="primary"
                            onClick={saveDateRange}
                            disabled={!startDate || !endDate}
                        >
                            Save
                        </Button>
                    </div>
                </Section>
            </Modal>
        </>
    );
};

export default DateFilter;
